import { Component, Signal, ChangeDetectionStrategy, viewChild, OnInit } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getHolidays, Region } from 'feiertagejs';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import 'moment/locale/de';
import { WebsiteStateService } from '../../../../../state-management/services/website-state.service';
import { WebsiteConfigOpeningHoursDisplayComponent } from './website-config-opening-hours-display/website-config-opening-hours-display.component';
import { WebsiteConfigFormExpansionPanelComponent } from '../website-config-form-expansion-panel/website-config-form-expansion-panel.component';
import { GermanState } from '../../../../../state-management/model/enums/state.enums';
import { BusinessConfiguration, SpecialDayOpeningHours, HolidayOpeningHours, HolidayDatesOpeningHours, OpeningPeriods, RegularOpeningHours, DifferentOpeningHoursNotification } from '../../../../../state-management/model/interfaces/state.model';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AdminUserInputComponent } from '../../../admin-user-input/admin-user-input.component';
import { GERMAN_STATE_ABBREVIATION_MAP, HOLIDAY_NAME_MAP } from '../../../../../state-management/constants/global.constants';


@Component({
  selector: 'app-website-config-form-opening-hours',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatDatepickerModule,
    FormsModule,
    MatInputModule,
    CommonModule,
    MatIcon,
    MatCheckbox,
    MatSelectModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatDividerModule,
    WebsiteConfigOpeningHoursDisplayComponent,
    WebsiteConfigFormExpansionPanelComponent,
    AdminUserInputComponent
  ],
  templateUrl: './website-config-form-opening-hours.component.html',
  styleUrl: './website-config-form-opening-hours.component.scss',
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    provideMomentDateAdapter(),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebsiteConfigFormOpeningHoursComponent implements OnInit {
  readonly picker = viewChild.required<MatDateRangePicker<Date>>('picker');

  public showHolidayDates: boolean = false;
  public range = new FormGroup({ start: new FormControl<Date | null>(null), end: new FormControl<Date | null>(null) });
  public metaData: Signal<BusinessConfiguration> = this.websiteState.configuration;
  public readonly states = Object.values(GermanState);
  public syncHolidayState = new FormControl(this.metaData().openingHours.holidayOpeningHours.stateHolidaySync);
  public stateHolidayControl = new FormControl(this.metaData().openingHours.holidayOpeningHours.stateHoliday);
  public notificationRange = new FormControl(this.metaData().openingHours.differentOpeningHoursNotification.notificationRange);
  
  private readonly _currentYear = new Date().getFullYear();
  readonly minDate = new Date();
  readonly maxDate = new Date(this._currentYear + 2, 11, 31);

  constructor(private websiteState: WebsiteStateService) { }

  ngOnInit(): void {
    this.updateFutureHolidayDates();
    this.updateFutureSpecialDayDates();
  }

  public openPicker(): void {
    this.picker().open();
  }

  public updateRegularOpeningHours(periods: OpeningPeriods[], index: number): void {
    const updatedRegularOpeningHours = this.metaData().openingHours.regularOpeningHours.map((item, i) =>
      i === index ? { ...item, periods: periods } : item
    );
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.websiteState.configuration().openingHours,
        regularOpeningHours: updatedRegularOpeningHours,
      },
    });
  }

  public updateHolidayDefaultOpeningHours(periods: OpeningPeriods[]): void {
    const updatedHolidayOpeningHours = {
      ...this.metaData().openingHours.holidayOpeningHours,
      defaultPeriods: periods,
      days: this.metaData().openingHours.holidayOpeningHours.days.map(day =>
        day.syncWithDefaultPeriods
          ? { ...day, periods: periods } // Aktualisiere periods, wenn sync aktiv ist
          : { ...day } // Behalte das ursprüngliche Objekt, wenn keine Änderung nötig ist
      ),
    };
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        holidayOpeningHours: updatedHolidayOpeningHours,
      },
    });
  }

  public updateHolidayDateOpeningHours(periods: OpeningPeriods[], index: number): void {
    const updatedHolidayOpeningHours = {
      ...this.metaData().openingHours.holidayOpeningHours,
      days: this.metaData().openingHours.holidayOpeningHours.days.map((day, i) => i === index
        ? { ...day, periods: periods }
        : { ...day }
      ),
    };
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        holidayOpeningHours: updatedHolidayOpeningHours,
      },
    });
  }

  public updateSpecialDayOpeningHours(periods: OpeningPeriods[], index: number): void {
    const updatedSpecialDayOpeningHours = this.metaData().openingHours.specialDayOpeningHours.map((day, i) =>
      i === index ? { ...day, periods: periods } : { ...day }
    );

    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        specialDayOpeningHours: updatedSpecialDayOpeningHours, // Bleibt ein Array
      },
    });
  }

  public updateSyncHoliday(value: boolean): void {
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        holidayOpeningHours: {
          ...this.metaData().openingHours.holidayOpeningHours,
          stateHolidaySync: value,
        },
      },
    });
  }

  public updateNotificationRange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement) {
      const value = inputElement.valueAsNumber;
      if (!isNaN(value)) {
        const updatedNotificationInfo: DifferentOpeningHoursNotification = {
          ...this.metaData().openingHours.differentOpeningHoursNotification,
          notificationRange: value,
        };
        this.websiteState.updateConfiguration({
          openingHours: {
            ...this.websiteState.configuration().openingHours,
            differentOpeningHoursNotification: updatedNotificationInfo,
          },
        });
      }
    }
  }

  public updateNotificationText(sanitizedHtml: string): void {
    const updatedNotificationInfo: DifferentOpeningHoursNotification = {
      ...this.metaData().openingHours.differentOpeningHoursNotification,
      notificationText: sanitizedHtml,
    };
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        differentOpeningHoursNotification: updatedNotificationInfo
      }
    });
  }

  public updateHolidayState(value: GermanState | null): void {
    const updatedHolidayOpeningHours: HolidayOpeningHours = {
      ...this.metaData().openingHours.holidayOpeningHours,
      stateHoliday: value,
    };
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.websiteState.configuration().openingHours,
        holidayOpeningHours: updatedHolidayOpeningHours,
      },
    });
    this.updateHolidayDates();
  }

  private updateHolidayDates(): void {
    const updatedHolidayOpeningHours = {
      ...this.metaData().openingHours.holidayOpeningHours,
    };
    const state = this.metaData().openingHours.holidayOpeningHours.stateHoliday;
    const updatedDays = state ? this.getHolidayForState(state).map((item1) => {
      const matchingItem = this.metaData().openingHours.holidayOpeningHours.days.find( // Vergleiche das "date" aus holidays mit den "date"-Werten in updatedHolidayOpeningHours.days
        (item2) => item2.date === item1.date
      );
      return matchingItem ? { ...matchingItem } : { ...item1 };
    }) : []; // Wenn kein Bundesland ausgewählt ist, wird die Liste geleert
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        holidayOpeningHours: {
          ...updatedHolidayOpeningHours,
          days: updatedDays,
        },
      },
    });
  }

  public updateSyncWithDefaultPeriods(value: boolean, index: number): void {
    const currentMetaData = this.metaData();
    const updatedHolidayOpeningHours = { ...currentMetaData.openingHours.holidayOpeningHours };
    const updatedDays = [...updatedHolidayOpeningHours.days];
    const updatedDay = { ...updatedDays[index] };
    updatedDay.syncWithDefaultPeriods = value;
    if (
      value &&
      updatedHolidayOpeningHours.defaultPeriods !== updatedDay.periods
    ) {
      updatedDay.periods = updatedHolidayOpeningHours.defaultPeriods;
    }
    updatedDays[index] = updatedDay;
    updatedHolidayOpeningHours.days = updatedDays;
    this.websiteState.updateConfiguration({
      openingHours: {
        ...currentMetaData.openingHours,
        holidayOpeningHours: updatedHolidayOpeningHours,
      },
    });
  }

  private updateFutureSpecialDayDates(): void { //checks for special days in the future and updates the list
    const updatedSpecialDayOpeningHours = this.sortSpecialDaysByDate(this.metaData().openingHours.specialDayOpeningHours).filter(item => this._isFutureDate(item.date));
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        specialDayOpeningHours: updatedSpecialDayOpeningHours,
      },
    });
  }

  private updateFutureHolidayDates(): void { //checks for holidays in the future and updates the list
    const updatedHolidayOpeningHours = this.metaData().openingHours.holidayOpeningHours;
    updatedHolidayOpeningHours.days = updatedHolidayOpeningHours.days.filter(item => this._isFutureDate(item.date));
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        holidayOpeningHours: updatedHolidayOpeningHours,
      },
    });
  }

  public addSpecialDays(): void {
    const startDate = this.range.value.start;
    const endDate = this.range.value.end;
    if (startDate && endDate) {
      let currentDate = new Date(startDate);
      const updatedSpecialDayOpeningHours: SpecialDayOpeningHours[] = this.metaData().openingHours.specialDayOpeningHours
      while (currentDate <= endDate) {
        const formattedDate = this.formatDate(currentDate);
        // Überprüfen, ob das Datum bereits existiert
        const existingIndex = updatedSpecialDayOpeningHours.findIndex(
          (day) => day.date === formattedDate
        );
        // Wenn vorhanden, entferne das ursprüngliche Element
        if (existingIndex !== -1) {
          updatedSpecialDayOpeningHours.splice(existingIndex, 1);
        }
        updatedSpecialDayOpeningHours.push({
          date: formattedDate,
          periods: [],
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      // Sortiere nach Datum
      const sortedOpeningHours = this.sortSpecialDaysByDate(updatedSpecialDayOpeningHours);
      this.websiteState.updateConfiguration({
        openingHours: {
          ...this.websiteState.configuration().openingHours,
          specialDayOpeningHours: sortedOpeningHours,
        },
      });
    }
  }

  public deleteSpecialDay(dayToDelete: SpecialDayOpeningHours): void {
    const updatedList = this.metaData().openingHours.specialDayOpeningHours.filter(
      (day) => day.date !== dayToDelete.date
    );
    this.websiteState.updateConfiguration({
      openingHours: {
        ...this.metaData().openingHours,
        specialDayOpeningHours: updatedList,
      },
    });
  }

  private getHolidayForState(state: GermanState): HolidayDatesOpeningHours[] {
    const abbr = GERMAN_STATE_ABBREVIATION_MAP[state];
    const currentYear = new Date().getFullYear();
    const holidays = getHolidays(currentYear, abbr).filter(holiday => holiday.date >= new Date()).concat(getHolidays(currentYear + 1, abbr));
    return holidays.map(holiday => {
      const displayName = HOLIDAY_NAME_MAP[holiday.name] || holiday.name;  // Wenn kein Mapping vorhanden ist, wird der Name beibehalten
      const formattedDate = this.formatDate(holiday.date);
      return {
        displayName: displayName,
        date: formattedDate,
        syncWithDefaultPeriods: true,
        periods: this.metaData().openingHours.holidayOpeningHours.defaultPeriods
      };
    });
  }

  public isHolidaySpecialDay(day: HolidayDatesOpeningHours): boolean {
    return this.metaData().openingHours.specialDayOpeningHours.some(specialDay => specialDay.date === day.date)
  }

  private sortSpecialDaysByDate(openingHours: SpecialDayOpeningHours[]): SpecialDayOpeningHours[] {
    return openingHours.sort((a, b) => {
      const [dayA, monthA, yearA] = a.date.split('.').map(Number);
      const [dayB, monthB, yearB] = b.date.split('.').map(Number);
      const dateA = new Date(yearA, monthA - 1, dayA); // Monat ist nullbasiert
      const dateB = new Date(yearB, monthB - 1, dayB);
      return dateA.getTime() - dateB.getTime(); // Aufsteigende Sortierung
    });
  }

  private formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`; // DD.MM.YYYY Format
  }

  private _isFutureDate(dateString: string): boolean {
    const [day, month, year] = dateString.split('.').map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }
}
